import React, { useState } from 'react';
import { observer } from 'src/utils/mobx-react';
import {
    SelectWrapper,
    SelectInputContainer,
    SelectInputLabel,
} from 'src/domains/players/webview/components/WithdrawAndDeposit/components/paymentMethodDropdown/PaymentMethodDropdown.style';
import {
    OptionTextContainer,
    SubtitleContainer,
    StyledCheckIcon,
    NewTrashIconWrapper,
    NewTrashIcon,
    Separator,
    SeparatorWrapper,
} from './NewPaymentMethodDropdown.style';
import { ChevronIconWrapper } from 'src/domains/layouts/webview/components/styles/baseDropdownStyle.style';
import { components, OptionProps, GroupBase } from 'react-select';
import { DeleteCardPopup } from 'src/domains/players/webview/components/WithdrawAndDeposit/components/DeleteCardPupup/DeleteCardPopup';
import { DeleteCardPopupState } from 'src/domains/players/webview/components/WithdrawAndDeposit/components/DeleteCardPupup/DeleteCardPopup.state';
import { NewPaymentMethodDropdownState, optionType, OptionType } from './NewPaymentMethodDropdown.state';
import { PaymentMethodDropdownState } from 'src/domains/players/webview/components/WithdrawAndDeposit';

type CustomOptionPropsType<T = OptionType> = OptionProps<T, false, GroupBase<T>>;
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { useCommon } from 'src/domains/common/Common';

export interface DropDownPropsType {
    allowAddingMethod?: boolean;
    label: string | JSX.Element;
    paymentMethodDropdownState: PaymentMethodDropdownState;
}

const NewPaymentMethodOption = observer(
    'NewPaymentMethodOption',
    ({
        optionProps,
        isActive,
        handleOpenRemoveCardPopup,
    }: {
        optionProps: CustomOptionPropsType;
        isActive: boolean;
        handleOpenRemoveCardPopup: (id: string, label: string, provider: string) => void;
    }) => {
        const { isDisabled, expires, label, value, netDeposit, isSeparator, provider } = optionProps.data;

        if (isSeparator)
            return (
                <SeparatorWrapper>
                    <Separator />
                </SeparatorWrapper>
            );
        else
            return (
                <components.Option {...optionProps}>
                    <OptionTextContainer>
                        <div>{label}</div>
                        <SubtitleContainer>
                            {isDisabled ? `Expired: ${expires ?? ''}` : `Net Deposit Balance: ${netDeposit ?? ''}`}
                        </SubtitleContainer>
                    </OptionTextContainer>
                    {isActive && <StyledCheckIcon />}
                    {isDisabled && !isActive ? (
                        <NewTrashIconWrapper
                            onClick={(): void => {
                                handleOpenRemoveCardPopup(value, label.replaceAll(' ', '').slice(-8), provider);
                            }}
                        >
                            <NewTrashIcon />
                        </NewTrashIconWrapper>
                    ) : null}
                </components.Option>
            );
    }
);

export const NewPaymentMethodDropdown = observer('NewPaymentMethodDropdown', (props: DropDownPropsType) => {
    const { allowAddingMethod = false, label, paymentMethodDropdownState } = props;
    const common = useCommon();
    const configState = ConfigComponents.get(common).config;

    const [deleteCardPopupState] = useState(
        () => new DeleteCardPopupState(common, paymentMethodDropdownState.refreshPaymentMethods)
    );
    const [newMethodState] = useState(
        () => new NewPaymentMethodDropdownState(allowAddingMethod, common, paymentMethodDropdownState)
    );

    return (
        <>
            <SelectInputContainer>
                <SelectInputLabel data-test='select-input-label'>{label}</SelectInputLabel>
                <SelectWrapper
                    newDropdown
                    value={newMethodState.activeElement}
                    options={newMethodState.allOptions}
                    className='custom-dropdown-select'
                    classNamePrefix='react-select'
                    onChange={newMethodState.onChange}
                    components={{
                        Option: (optionProps): JSX.Element | null => {
                            const option = optionType.safeParse(optionProps.data);
                            if (!option.success) return null;

                            const isActive = option.data.value === newMethodState.activeElement?.value;

                            const typedProps = Object.assign(
                                Object.create(Object.getPrototypeOf(optionProps)),
                                optionProps
                            );
                            typedProps.data = option.data;

                            return (
                                <NewPaymentMethodOption
                                    optionProps={typedProps}
                                    handleOpenRemoveCardPopup={deleteCardPopupState.handleOpenRemoveCardPopup}
                                    isActive={isActive}
                                />
                            );
                        },
                        DropdownIndicator: (): JSX.Element => <ChevronIconWrapper position='down' />,
                    }}
                    isSearchable={false}
                    variant={configState.componentVariants.accountSectionVariant}
                />
            </SelectInputContainer>
            <DeleteCardPopup deleteCardPopupState={deleteCardPopupState} />
        </>
    );
});
